import { useMemo, useRef, useState } from 'react';
import { FontIcon } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import {
  FilterType,
  PagedDataGrid,
  useMutation,
  type DataGridColumn,
  type ICommandBarItem,
  type IFilterableColumn,
  type IPagedDataGridRefType,
} from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import ConfirmDialog from '../../common/ConfirmDialog';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  deleteCompanyHoliday,
  getCompanyHolidays,
  type ICompanyHoliday,
} from '../../services/companyHolidayService';
import { formatDateOnlyForDisplay } from '../../utils/dateOnly';

const columns: DataGridColumn<ICompanyHoliday>[] = [
  { key: 'name', name: 'Name', minWidth: 160, width: 280, isSortable: true },
  {
    key: 'holidayDate',
    name: 'Date',
    minWidth: 120,
    width: 160,
    isSortable: true,
    value: (item) => formatDateOnlyForDisplay(item.holidayDate),
  },
  {
    key: 'region',
    name: 'Region',
    minWidth: 160,
    width: 260,
    isSortable: true,
  },
];

const filterColumns: IFilterableColumn[] = [
  { key: 'name', label: 'Name', type: FilterType.String },
  { key: 'region', label: 'Region', type: FilterType.String },
  { key: 'holidayDate', label: 'Date', type: FilterType.Date },
];

const CompanyHolidays = () => {
  const navigate = useNavigate();
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const [selected, setSelected] = useState<ICompanyHoliday>();
  const [isDeleteOpen, { setTrue: showDelete, setFalse: hideDelete }] =
    useBoolean(false);
  const remove = useMutation(
    (holiday: ICompanyHoliday, abortController: AbortController) =>
      deleteCompanyHoliday(abortController, holiday.id)
  );

  const commands: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'newHoliday',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => {
          void navigate('/newHoliday');
        },
      },
      {
        key: 'editHoliday',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => {
          if (selected) void navigate(`/editHoliday?id=${selected.id}`);
        },
        disabled: !selected,
      },
      {
        key: 'deleteHoliday',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: showDelete,
        disabled: !selected || remove.loading,
      },
    ],
    [navigate, selected, showDelete, remove.loading]
  );

  const confirmDelete = async () => {
    hideDelete();
    if (!selected) return;
    const outcome = await remove.mutate(selected);
    if (outcome.status === 'success') {
      gridRef.current?.refresh();
      setSuccessMessage(`Holiday ${selected.name} deleted.`);
      setSelected(undefined);
    }
    if (outcome.status === 'error') {
      setErrorMessage(
        getApiErrorMessage(outcome.error, 'Failed to delete holiday.')
      );
    }
  };

  return (
    <>
      <PagedDataGrid<ICompanyHoliday>
        ref={gridRef}
        columns={columns}
        getAction={getCompanyHolidays}
        commandBarItems={commands}
        selectionMode="single"
        selectedId={selected?.id ?? null}
        onSelectionChange={setSelected}
        searchable={false}
        filterEnabled
        filterColumns={filterColumns}
        empty={{
          title: 'No holidays yet!',
          action: {
            text: 'Add holiday',
            onClick: () => {
              void navigate('/newHoliday');
            },
          },
        }}
      />
      <ConfirmDialog
        isModalOpen={isDeleteOpen}
        hideModal={hideDelete}
        message={`Holiday ${
          selected?.name ?? ''
        } would be deleted, are you sure?`}
        onYesClick={confirmDelete}
      />
    </>
  );
};

export default CompanyHolidays;
